/**
 * Page Denominator Step Component
 *
 * The Page Setup Wizard's % denominator step, shown only when Page Details sets a denominator label and
 * titled with that label (e.g. "% Gross Margin"). It edits the page's denominator drivers in the same
 * fields as the Page Driver step, one tab per page dataset. Only the datasets in DENOMINATOR_DATASETS
 * (Galaxy) are editable; any other dataset's tab says it is not enabled yet and is never saved.
 */

import { DATASET_DEFAULT_PAGE_DRIVERS, DENOMINATOR_DATASETS, denominatorNotEnabledMessage } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import Alert from '@mui/material/Alert';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { AnyKPIDrivers } from 'types/PageSidePanel.types';

import DatasetDriverSetsEditor from './DatasetDriverSetsEditor';

const PageDenominatorStep = () => {
  const {
    denominatorDriverSets,
    setDenominatorDriverSets,
    pageDriverSets,
    enabledDatasets,
    denominatorDatasetCounts,
    pageError,
  } = usePageSetupContext();
  const { toastNotification } = useAurigaContext();

  /** The page's datasets, so the tabs match the Page Driver step's. */
  const pageDatasets = useMemo(
    () => (Object.keys(pageDriverSets) as DatasetSource[]).filter((ds) => !isNil(pageDriverSets[ds])),
    [pageDriverSets],
  );

  /** A new denominator set starts from the dataset's page drivers. */
  const defaultsFor = useCallback(
    (dataset: DatasetSource): AnyKPIDrivers => pageDriverSets[dataset] ?? DATASET_DEFAULT_PAGE_DRIVERS[dataset],
    [pageDriverSets],
  );

  /** Blocks removing a set that base denominators on the page resolve through. */
  const deleteGuard = useCallback(
    (dataset: DatasetSource) => {
      const dependentCount = denominatorDatasetCounts[dataset] ?? 0;
      if (dependentCount === 0) return null;
      return `Can't remove the ${resolveDatasetSourceLabel(dataset)} denominator drivers: ${dependentCount} deckline${
        dependentCount === 1 ? ' has a base denominator' : 's have base denominators'
      } that use${dependentCount === 1 ? 's' : ''} them. Change ${dependentCount === 1 ? 'it' : 'them'} first.`;
    },
    [denominatorDatasetCounts],
  );

  const handleDeleteBlocked = useCallback(
    (reason: string) => toastNotification({ message: reason, type: 'error', visible: true }),
    [toastNotification],
  );

  if (pageError) {
    return <Alert severity='error'>{pageError}</Alert>;
  }

  return (
    <DatasetDriverSetsEditor
      driverSets={denominatorDriverSets}
      onChange={setDenominatorDriverSets}
      enabledDatasets={enabledDatasets}
      allowedDatasets={DENOMINATOR_DATASETS}
      shownDatasets={pageDatasets}
      defaultsFor={defaultsFor}
      deleteGuard={deleteGuard}
      onDeleteBlocked={handleDeleteBlocked}
      deleteLabel='Delete Source from Denominator Drivers'
      notAllowedMessage={denominatorNotEnabledMessage}
      idPrefix='denominator-drivers'
    />
  );
};

export default PageDenominatorStep;
